// BRIEF 92 §2c — THE FIRM'S WEBSITE, TURNED INTO AN href OR INTO NOTHING.
//
// `FirmSettings.firm.website` is a free-text field (`SettingsView.tsx`, label
// `settings.label.website`). A firm types `watt.legal`. Three things follow, and
// only the first is the one §2c asks about:
//
//   1. A BARE HOST IS NORMALISED to `https://`. Left alone it is a RELATIVE
//      url — `href="watt.legal"` navigates this SPA to `/watt.legal`, a route
//      that does not exist, so "left to fail" would fail INSIDE the app rather
//      than in a new tab. Normalising is the only option that fails outward.
//   2. ANY OTHER SCHEME IS REFUSED. This value is FIRM-AUTHORED, which
//      RULINGS 2026-08-02 (04e) rules is not the same as app-internal:
//      `javascript:` in an href is script execution, and `data:` is a document
//      of the author's choosing. Neither is normalised into safety — they
//      return null and the caller renders plain text.
//   3. NULL IS A REAL ANSWER, so §2a's "no empty anchor, no dead cursor" is
//      structural rather than a second check at the call site: empty, unset,
//      whitespace and refused values all come back the same way.
//
// WHY NOT `openDocHref` (SharedUI.tsx). That guard answers a CLICK on arbitrary
// free text pasted into a document row, with toasts, and it normalises only a
// bare `www.` — a bare `watt.legal` is "not valid" there, which is right for a
// field that could hold anything and wrong for one labelled "Website". Same
// policy on schemes, different job: this one builds an href and returns nothing
// rather than explaining itself to a user who is not clicking.

/** The href for a firm's website field, or `null` when there is nothing safe to
 *  link to. Callers render plain text on null. */
export function firmWebsiteHref(raw: unknown): string | null {
  /* C0 controls come OUT before anything is decided, because the URL parser
     removes tab, LF and CR from a href and only then reads the scheme
     (WHATWG URL, "URL parsing", the leading/interior C0 strip). So
     `java\tscript:alert(1)` is `javascript:alert(1)` to a browser and is NOT
     one to a naive regex — the scheme test below has to see what the browser
     will see. Interior SPACES are left alone: they never make a scheme, and
     removing them would silently repair a firm's typo into a different host. */
  const v = String(raw ?? "")
    .replace(/[\u0000-\u001f\u007f]/g, "")
    .trim();
  if (!v) return null;
  // Already addressed, and to a scheme this app will follow.
  if (/^https?:\/\//i.test(v)) return v;
  // Any OTHER scheme — mailto:, tel:, data:, file:, javascript: — is refused
  // rather than repaired. A scheme is `alpha *( alpha / digit / "+" / "-" / "." )`
  // followed by ":" (RFC 3986 §3.1); a host with a port ("watt.legal:8443")
  // does not match it, because a host is not alpha-led-then-colon at position 0
  // unless it has no dot, and a schemeless "localhost:3000" is the one
  // ambiguity — resolved AGAINST linking, which is the safe direction.
  if (/^[a-z][a-z0-9+.-]*:/i.test(v)) return null;
  // "/x" is app-internal, "//host" is protocol-relative. Neither is a firm's
  // website and both would navigate somewhere the firm did not name.
  if (v.startsWith("/")) return null;
  return "https://" + v;
}
